import { useState, useEffect } from "react";
import { uploadResourceFile, announceResources } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Progress } from "../ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

const EMPTY = { files: [], title: "", description: "", notify: true, message: "" };

export const UploadSlidesDialog = ({ open, onOpenChange, folders, category, defaultFolderId, onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [folderId, setFolderId] = useState(defaultFolderId || "");
  const [progress, setProgress] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const single = form.files.length === 1;

  useEffect(() => {
    if (open) { setForm(EMPTY); setFolderId(defaultFolderId || folders[0]?.id || "none"); setProgress(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    if (!form.files.length) return toast.error("Choose at least one .pptx file");
    if (form.notify && !form.message.trim()) return toast.error("Write a message for partners, or turn off notify");
    const ids = [];
    try {
      for (let i = 0; i < form.files.length; i++) {
        const fd = new FormData();
        fd.append("file", form.files[i]);
        fd.append("folder_id", folderId === "none" ? "" : folderId);
        fd.append("category", category);
        if (single) { fd.append("title", form.title); fd.append("description", form.description); }
        const { data } = await uploadResourceFile(fd, (e) =>
          setProgress(Math.round(((i + (e.loaded / (e.total || 1))) / form.files.length) * 100)));
        ids.push(data.id);
      }
      let note = "";
      if (form.notify) {
        const { data } = await announceResources({ message: form.message, folder_id: folderId === "none" ? null : folderId, resource_ids: ids });
        note = ` · ${data.recipients} partners notified`;
      }
      toast.success(`${ids.length} file${ids.length === 1 ? "" : "s"} uploaded${note}`);
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      if (ids.length) onDone();
    } finally {
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upload-slides-dialog">
        <DialogHeader><DialogTitle>Upload slides</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>PowerPoint files (.pptx)</Label>
            <Input type="file" accept=".pptx" multiple data-testid="upload-slides-file"
              onChange={(e) => set("files", Array.from(e.target.files || []))} /></div>
          <div><Label>Folder</Label>
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger data-testid="upload-slides-folder"><SelectValue placeholder="Choose a folder" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No folder</SelectItem>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select></div>
          {single && <>
            <div><Label>Title (optional)</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder={form.files[0].name.replace(/\.pptx$/i, "")} data-testid="upload-slides-title" /></div>
            <div><Label>Description (optional)</Label><Textarea rows={2} value={form.description}
              onChange={(e) => set("description", e.target.value)} data-testid="upload-slides-description" /></div>
          </>}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><p className="text-sm font-medium">Notify partners</p>
              <p className="text-xs text-muted-foreground">Email + in-app alert to every partner rep and manager</p></div>
            <Switch checked={form.notify} onCheckedChange={(v) => set("notify", v)} data-testid="upload-slides-notify" />
          </div>
          {form.notify && <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)}
            placeholder="New Holiday 2026 slides just dropped — grab them for your Q4 pitches." data-testid="upload-slides-message" />}
          {progress !== null && <Progress value={progress} data-testid="upload-slides-progress" />}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={upload} disabled={progress !== null} data-testid="upload-slides-submit">
              {progress !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />Upload</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
